import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";

const JobCommsWebSocketContext = createContext(null);

export const useJobCommsWebSocket = () => useContext(JobCommsWebSocketContext);

export const JobCommsWebSocketProvider = ({ children, userId }) => {
  const wsRef = useRef(null);
  const reconnectTimeout = useRef(null);
  const reconnectAttempts = useRef(0);
  const activeRooms = useRef(new Set());
  const listenersRef = useRef(new Map());
  const [isConnected, setIsConnected] = useState(false);

  const getWsUrl = useCallback(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "https://shootzu.com";
    const wsProtocol = backendUrl.startsWith("https") ? "wss" : "ws";
    const host = backendUrl.replace(/^https?:\/\//, "");
    return `${wsProtocol}://${host}/ws/job-comms/${userId}`;
  }, [userId]);

  const connect = useCallback(() => {
    if (!userId) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    try {
      const ws = new WebSocket(getWsUrl());

      ws.onopen = () => {
        setIsConnected(true);
        reconnectAttempts.current = 0;
        // Rejoin active rooms
        activeRooms.current.forEach((jobId) => {
          ws.send(JSON.stringify({ type: "join_job", job_id: jobId }));
        });
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          // Notify all listeners for this message type
          const callbacks = listenersRef.current.get(data.type);
          if (callbacks) {
            callbacks.forEach((cb) => cb(data));
          }
          // Also notify wildcard listeners
          const wildcardCallbacks = listenersRef.current.get("*");
          if (wildcardCallbacks) {
            wildcardCallbacks.forEach((cb) => cb(data));
          }
        } catch (e) {
          // ignore parse errors
        }
      };

      ws.onclose = () => {
        setIsConnected(false);
        // Auto-reconnect with exponential backoff
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
        reconnectAttempts.current++;
        reconnectTimeout.current = setTimeout(connect, delay);
      };

      ws.onerror = () => {
        ws.close();
      };

      wsRef.current = ws;
    } catch (e) {
      // retry
      const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
      reconnectAttempts.current++;
      reconnectTimeout.current = setTimeout(connect, delay);
    }
  }, [userId, getWsUrl]);

  useEffect(() => {
    connect();

    // Heartbeat every 30s
    const heartbeat = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: "ping" }));
      }
    }, 30000);

    return () => {
      clearInterval(heartbeat);
      clearTimeout(reconnectTimeout.current);
      if (wsRef.current) {
        wsRef.current.onclose = null; // prevent reconnect on intentional close
        wsRef.current.close();
      }
    };
  }, [connect]);

  const joinJob = useCallback((jobId) => {
    activeRooms.current.add(jobId);
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "join_job", job_id: jobId }));
    }
  }, []);

  const leaveJob = useCallback((jobId) => {
    activeRooms.current.delete(jobId);
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "leave_job", job_id: jobId }));
    }
  }, []);

  const sendMessage = useCallback((jobId, conversationId, text) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          type: "send_message",
          job_id: jobId,
          conversation_id: conversationId,
          text,
        })
      );
    }
  }, []);

  const sendTyping = useCallback((jobId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "typing", job_id: jobId }));
    }
  }, []);

  const sendStopTyping = useCallback((jobId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "stop_typing", job_id: jobId }));
    }
  }, []);

  const markRead = useCallback((conversationId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({ type: "mark_read", conversation_id: conversationId })
      );
    }
  }, []);

  const on = useCallback((eventType, callback) => {
    if (!listenersRef.current.has(eventType)) {
      listenersRef.current.set(eventType, new Set());
    }
    listenersRef.current.get(eventType).add(callback);
    // Return unsubscribe function
    return () => {
      listenersRef.current.get(eventType)?.delete(callback);
    };
  }, []);

  const value = {
    isConnected,
    joinJob,
    leaveJob,
    sendMessage,
    sendTyping,
    sendStopTyping,
    markRead,
    on,
  };

  return (
    <JobCommsWebSocketContext.Provider value={value}>
      {children}
    </JobCommsWebSocketContext.Provider>
  );
};
